import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeft, Plus, Printer, Search } from 'lucide-react';
import { AGEING_BUCKETS, formatMoney, NON_ACCOUNT_METHODS, PAYMENT_METHODS, type AgeingKey, type CurrencyCode, type PaymentMethod } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Button, Checkbox, Dialog, EmptyState, ErrorNotice, Field, FormError, PageHeader, Panel, Select, Spinner, Switch, TextArea, TextInput, fieldErrors, formatDateTime, useToast } from '../../ui';
import { MoneyInput, useDebounced } from '../../ui/inputs';

export interface Customer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  idNumber: string | null;
  tin: string | null;
  vatNumber: string | null;
  address: string | null;
  creditLimitCents: number;
  paymentTermsDays: number;
  notes: string | null;
  isActive: boolean;
  balanceCents: number;
  overdueCents: number;
  availableCreditCents: number;
}

export function CustomersPage() {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [q, setQ] = useState('');
  const [owing, setOwing] = useState(false);
  const [adding, setAdding] = useState(false);
  const dq = useDebounced(q.trim(), 200);
  const res = useQuery({ queryKey: ['customers', dq, owing], queryFn: () => api.get<Customer[]>(`/api/customers?q=${encodeURIComponent(dq)}${owing ? '&owing=true' : ''}`) });
  const total = (res.data ?? []).reduce((a, c) => a + Math.max(c.balanceCents, 0), 0);
  const overdue = (res.data ?? []).reduce((a, c) => a + c.overdueCents, 0);
  return (
    <>
      <PageHeader
        title="Customers"
        description="Regular customers, their credit limits and what they owe."
        actions={can('customers.manage') && <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>Add customer</Button>}
      />
      <Panel>
        <div className="flex flex-wrap items-center gap-4 border-b border-line px-5 py-3">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <TextInput aria-label="Search customers" className="pl-9" placeholder="Name or phone" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Checkbox label="Only customers who owe money" checked={owing} onChange={setOwing} />
          {res.data && (
            <span className="ml-auto text-sm text-muted">
              Owed <span className="font-medium text-ink num">{formatMoney(total, cur)}</span>
              {overdue > 0 && <>, <span className="font-medium text-danger num">{formatMoney(overdue, cur)}</span> overdue</>}
            </span>
          )}
        </div>
        {res.isLoading && <Spinner />}
        {res.error && <div className="p-4"><ErrorNotice error={res.error} /></div>}
        {res.data?.length === 0 && <EmptyState title="No customers found" />}
        {!!res.data?.length && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 text-right font-medium">Credit limit</th>
                  <th className="px-3 py-2.5 text-right font-medium">Owes</th>
                  <th className="px-5 py-2.5 text-right font-medium">Overdue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {res.data.map((c) => (
                  <tr key={c.id} className="hover:bg-ledger-50/60">
                    <td className="px-5 py-3">
                      <Link to={`/customers/${c.id}`} className="font-medium text-ledger-700 hover:underline">{c.name}</Link>
                      <span className="block text-xs text-muted">{c.phone ?? '—'}{c.creditLimitCents > 0 ? ` — ${c.paymentTermsDays} day terms` : ''}</span>
                    </td>
                    <td className="px-3 py-3 text-right num">{c.creditLimitCents ? formatMoney(c.creditLimitCents, cur) : 'Cash only'}</td>
                    <td className="px-3 py-3 text-right num">{formatMoney(c.balanceCents, cur)}</td>
                    <td className={clsx('px-5 py-3 text-right num', c.overdueCents > 0 && 'font-medium text-danger')}>{c.overdueCents ? formatMoney(c.overdueCents, cur) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {adding && <CustomerDialog customer={null} onClose={() => setAdding(false)} />}
    </>
  );
}

export function CustomerDialog({ customer, onClose, onSaved }: { customer: Customer | null; onClose: () => void; onSaved?: (c: Customer) => void }) {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({
    name: customer?.name ?? '',
    phone: customer?.phone ?? '',
    email: customer?.email ?? '',
    idNumber: customer?.idNumber ?? '',
    tin: customer?.tin ?? '',
    vatNumber: customer?.vatNumber ?? '',
    address: customer?.address ?? '',
    creditLimitCents: customer?.creditLimitCents ?? 0,
    paymentTermsDays: customer?.paymentTermsDays ?? 30,
    notes: customer?.notes ?? '',
    isActive: customer?.isActive ?? true,
  });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const m = useMutation({
    mutationFn: () => (customer ? api.put<Customer>(`/api/customers/${customer.id}`, v) : api.post<Customer>('/api/customers', v)),
    onSuccess: (c) => {
      toast(customer ? 'Customer saved' : 'Customer added');
      void qc.invalidateQueries({ queryKey: ['customers'] });
      void qc.invalidateQueries({ queryKey: ['customer'] });
      onSaved?.({ ...c, balanceCents: customer?.balanceCents ?? 0, overdueCents: customer?.overdueCents ?? 0, availableCreditCents: c.creditLimitCents - (customer?.balanceCents ?? 0) });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  const canCredit = can('credit.grant');
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={customer ? customer.name : 'Add customer'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="customer-form" busy={m.isPending}>{customer ? 'Save customer' : 'Add customer'}</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <form id="customer-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field label="Name" htmlFor="c-name" error={f.name}>
          <TextInput id="c-name" value={v.name} onChange={(e) => set('name', e.target.value)} invalid={!!f.name} autoFocus />
        </Field>
        <Field label="Phone" htmlFor="c-phone" error={f.phone}>
          <TextInput id="c-phone" type="tel" value={v.phone} onChange={(e) => set('phone', e.target.value)} invalid={!!f.phone} placeholder="+263 77 123 4567" />
        </Field>
        <Field label="Email" htmlFor="c-email" error={f.email}>
          <TextInput id="c-email" type="email" value={v.email} onChange={(e) => set('email', e.target.value)} invalid={!!f.email} />
        </Field>
        <Field label="National ID number" htmlFor="c-id" error={f.idNumber}>
          <TextInput id="c-id" value={v.idNumber} onChange={(e) => set('idNumber', e.target.value)} placeholder="e.g. 63-123456-A-42" />
        </Field>
        <Field label="TIN (businesses)" htmlFor="c-tin" error={f.tin} hint="Printed on their fiscal invoices">
          <TextInput id="c-tin" inputMode="numeric" value={v.tin} onChange={(e) => set('tin', e.target.value.replace(/\D/g, ''))} placeholder="10 digits" />
        </Field>
        <Field label="VAT number (businesses)" htmlFor="c-vat" error={f.vatNumber}>
          <TextInput id="c-vat" value={v.vatNumber} onChange={(e) => set('vatNumber', e.target.value)} />
        </Field>
        <Field label="Address" htmlFor="c-address" className="sm:col-span-2">
          <TextInput id="c-address" value={v.address} onChange={(e) => set('address', e.target.value)} />
        </Field>
        <Field label="Credit limit" htmlFor="c-limit" error={f.creditLimitCents} hint={canCredit ? '0 means cash sales only' : 'Only staff who can grant credit may change this'}>
          <MoneyInput id="c-limit" currency={cur} value={v.creditLimitCents} onChange={(c) => set('creditLimitCents', c ?? 0)} />
        </Field>
        <Field label="Days to pay" htmlFor="c-terms" error={f.paymentTermsDays}>
          <TextInput id="c-terms" inputMode="numeric" value={String(v.paymentTermsDays)} disabled={!canCredit} onChange={(e) => set('paymentTermsDays', Number(e.target.value.replace(/\D/g, '') || 0))} />
        </Field>
        <Field label="Notes" htmlFor="c-notes" className="sm:col-span-2">
          <TextArea id="c-notes" rows={2} value={v.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
        {customer && (
          <div className="flex items-center justify-between gap-4 rounded-[8px] bg-paper px-4 py-3 sm:col-span-2">
            <span>
              <span className="block font-medium">Account open</span>
              <span className="text-sm text-muted">A closed account can't buy on credit. History is kept.</span>
            </span>
            <Switch checked={v.isActive} onChange={(x) => set('isActive', x)} label="Account open" />
          </div>
        )}
      </form>
    </Dialog>
  );
}

interface Statement {
  customer: Customer;
  balanceCents: number;
  ageing: Record<AgeingKey, number> & { total: number; overdue: number };
  entries: { id: number; kind: string; amountCents: number; dueDate: string | null; refType: string | null; refId: string | null; refNumber: string | null; method: string | null; reference: string | null; note: string | null; createdAt: string; userName: string | null; balanceCents: number }[];
}

const KIND_LABELS: Record<string, string> = {
  sale: 'Sale on account',
  payment: 'Payment received',
  return: 'Goods returned',
  void: 'Sale voided',
  opening_balance: 'Opening balance',
  adjustment: 'Adjustment',
  credit_note: 'Credit note',
  invoice: 'Supplier invoice',
};

export function CustomerDetailPage() {
  const { id } = useParams();
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const q = useQuery({ queryKey: ['customer', id], queryFn: () => api.get<Statement>(`/api/customers/${id}`) });
  const [paying, setPaying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNotice error={q.error} />;
  const st = q.data!;
  const c = st.customer;
  return (
    <>
      <Link to="/customers" className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-ledger-700 hover:underline">
        <ArrowLeft className="size-4" /> All customers
      </Link>
      <PageHeader
        title={c.name}
        description={[c.phone, c.address, c.creditLimitCents ? `Limit ${formatMoney(c.creditLimitCents, cur)}, ${c.paymentTermsDays} days to pay` : 'Cash customer'].filter(Boolean).join(' — ')}
        actions={
          <>
            <Button icon={<Printer className="size-4" />} onClick={() => window.print()}>Print statement</Button>
            {can('customers.manage') && <Button onClick={() => setEditing(true)}>Edit</Button>}
            {can('credit.grant') && <Button onClick={() => setAdjusting(true)}>Adjust balance</Button>}
            {can('credit.receive_payment') && <Button variant="primary" onClick={() => setPaying(true)}>Receive payment</Button>}
          </>
        }
      />
      <div className="print-area space-y-6">
        <p className="hidden print:block">
          Statement for {c.name} from {me.business.tradingName ?? me.business.name}, {new Date().toLocaleDateString('en-GB')}
        </p>
        <div className="grid gap-px overflow-hidden rounded-[var(--radius-panel)] border border-line bg-line sm:grid-cols-3 lg:grid-cols-6">
          <div className="bg-surface p-4 sm:col-span-3 lg:col-span-1">
            <p className="text-sm text-muted">Owes now</p>
            <p className="text-2xl font-semibold num">{formatMoney(st.balanceCents, cur)}</p>
          </div>
          {AGEING_BUCKETS.map((b) => (
            <div key={b.key} className="bg-surface p-4">
              <p className="text-sm text-muted">{b.label}</p>
              <p className={clsx('text-lg font-semibold num', b.key !== 'current' && st.ageing[b.key] > 0 && 'text-danger')}>{formatMoney(st.ageing[b.key], cur)}</p>
            </div>
          ))}
        </div>
        <Panel title="Statement">
          {st.entries.length === 0 ? (
            <EmptyState title="No account activity yet" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="border-b border-line text-sm text-muted">
                    <th className="px-5 py-2 font-medium">Date</th>
                    <th className="px-3 py-2 font-medium">Details</th>
                    <th className="px-3 py-2 text-right font-medium">Charged</th>
                    <th className="px-3 py-2 text-right font-medium">Paid / credited</th>
                    <th className="px-5 py-2 text-right font-medium">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {st.entries.map((e) => (
                    <tr key={e.id}>
                      <td className="px-5 py-2 text-sm text-muted num">{formatDateTime(e.createdAt)}</td>
                      <td className="px-3 py-2">
                        {KIND_LABELS[e.kind] ?? e.kind}{' '}
                        {e.refType === 'sale' && e.refId ? <Link className="text-ledger-700 hover:underline" to={`/sales/${e.refId}`}>{e.refNumber}</Link> : e.refNumber}
                        <span className="block text-xs text-muted">
                          {[e.method && (PAYMENT_METHODS[e.method as PaymentMethod]?.label.split(' (')[0] ?? e.method), e.reference, e.note, e.dueDate && e.amountCents > 0 ? `due ${e.dueDate}` : null].filter(Boolean).join(' — ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right num">{e.amountCents > 0 ? formatMoney(e.amountCents, cur) : ''}</td>
                      <td className="px-3 py-2 text-right num">{e.amountCents < 0 ? formatMoney(-e.amountCents, cur) : ''}</td>
                      <td className="px-5 py-2 text-right font-medium num">{formatMoney(e.balanceCents, cur)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
      {paying && <PaymentDialog kind="customer" id={c.id} name={c.name} onClose={() => setPaying(false)} />}
      {editing && <CustomerDialog customer={{ ...c, balanceCents: st.balanceCents, overdueCents: st.ageing.overdue, availableCreditCents: 0 }} onClose={() => setEditing(false)} />}
      {adjusting && <AdjustBalanceDialog kind="customer" id={c.id} name={c.name} onClose={() => setAdjusting(false)} />}
    </>
  );
}

/** Receive a customer payment, or pay a supplier. */
export function PaymentDialog({ kind, id, name, onClose }: { kind: 'customer' | 'supplier'; id: string; name: string; onClose: () => void }) {
  const { me } = useSession();
  const { branch } = useCurrentBranch();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState<PaymentMethod>(kind === 'customer' ? 'cash' : 'bank_transfer');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [fromTill, setFromTill] = useState(false);
  const m = useMutation({
    mutationFn: () =>
      api.post<{ number: string; balanceCents: number }>(`/api/${kind === 'customer' ? 'customers' : 'suppliers'}/${id}/payments`, {
        branchId: branch?.id,
        amountCents: amount,
        method,
        reference: reference || null,
        note: note || null,
        ...(kind === 'supplier' ? { fromTill: method === 'cash' && fromTill } : {}),
      }),
    onSuccess: (r) => {
      toast(`${r.number} recorded. ${kind === 'customer' ? `${name} now owes` : 'Still owed'} ${formatMoney(r.balanceCents, cur)}`);
      for (const k of ['customer', 'customers', 'supplier-statement', 'supplier-balances', 'current-shift', 'dashboard']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title={kind === 'customer' ? `Payment from ${name}` : `Pay ${name}`}
      description={kind === 'customer' && method === 'cash' ? 'Cash goes into your open till.' : undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!amount} busy={m.isPending} onClick={() => m.mutate()}>Record payment</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Amount" htmlFor="pay-amt" error={f.amountCents}>
          <MoneyInput id="pay-amt" currency={cur} value={amount} onChange={setAmount} autoFocus />
        </Field>
        <Field label="Paid by" htmlFor="pay-method">
          <Select id="pay-method" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {NON_ACCOUNT_METHODS.map((x) => <option key={x} value={x}>{PAYMENT_METHODS[x].label}</option>)}
          </Select>
        </Field>
        {PAYMENT_METHODS[method].needsReference && (
          <Field label="Reference" htmlFor="pay-ref" error={f.reference}>
            <TextInput id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
          </Field>
        )}
        <Field label="Note" htmlFor="pay-note">
          <TextInput id="pay-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        {kind === 'supplier' && method === 'cash' && (
          <div className="sm:col-span-2">
            <Checkbox label="Take the cash from my open till" checked={fromTill} onChange={setFromTill} />
          </div>
        )}
      </div>
    </Dialog>
  );
}

export function AdjustBalanceDialog({ kind, id, name, onClose }: { kind: 'customer' | 'supplier'; id: string; name: string; onClose: () => void }) {
  const { me } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const [type, setType] = useState<'opening_balance' | 'adjustment' | 'credit_note'>('opening_balance');
  const [direction, setDirection] = useState<'up' | 'down'>('up');
  const [amount, setAmount] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api.post(`/api/${kind === 'customer' ? 'customers' : 'suppliers'}/${id}/adjustments`, {
        kind: type,
        amountCents: type === 'credit_note' || direction === 'down' ? -(amount ?? 0) : amount,
        note,
        reference: reference || null,
      }),
    onSuccess: () => {
      toast('Balance adjusted');
      for (const k of ['customer', 'customers', 'supplier-statement', 'supplier-balances']) void qc.invalidateQueries({ queryKey: [k] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Adjust ${name}'s balance`}
      description="For balances brought forward from your old books, credit notes and corrections. Recorded in the audit log."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!amount || note.trim().length < 3} busy={m.isPending} onClick={() => m.mutate()}>Save</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Type" htmlFor="adj-type">
          <Select id="adj-type" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
            <option value="opening_balance">Balance brought forward</option>
            <option value="credit_note">Credit note</option>
            <option value="adjustment">Correction</option>
          </Select>
        </Field>
        {type !== 'credit_note' && (
          <Field label="Effect" htmlFor="adj-dir">
            <Select id="adj-dir" value={direction} onChange={(e) => setDirection(e.target.value as typeof direction)}>
              <option value="up">{kind === 'customer' ? 'They owe more' : 'We owe more'}</option>
              <option value="down">{kind === 'customer' ? 'They owe less' : 'We owe less'}</option>
            </Select>
          </Field>
        )}
        <Field label="Amount" htmlFor="adj-amt">
          <MoneyInput id="adj-amt" currency={cur} value={amount} onChange={setAmount} />
        </Field>
        <Field label="Reference" htmlFor="adj-ref">
          <TextInput id="adj-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
        </Field>
        <Field label="Explanation" htmlFor="adj-note" className="sm:col-span-2">
          <TextInput id="adj-note" value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

